/* Packer screen (phone-first): search client → take Inside / Outside packing photos → posted to the parcel's query thread. */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, CheckCircle2, Image as ImageIcon, LayoutList, Loader2, LogOut, Package, Search, Send, X } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhotoRequestsList } from "@/components/PhotoRequestsList";

const Shot = ({ side, file, setFile, tid }) => {
  const ref = useRef(null);
  const url = file ? URL.createObjectURL(file) : "";
  return (
    <div className="relative flex-1">
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`${tid}-${side}-input`}
        onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
      {file ? (
        <div className="relative h-28 overflow-hidden rounded-xl border-2 border-teal-500">
          <img src={url} alt={side} className="h-full w-full object-cover" />
          <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 text-[10px] font-semibold uppercase text-white">{side}</span>
          <button type="button" onClick={() => setFile(null)} className="absolute right-1 top-1 rounded-full bg-white p-1 shadow" title="Retake" data-testid={`${tid}-${side}-clear`}><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} data-testid={`${tid}-${side}-btn`}
          className="flex h-28 w-full flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-teal-400 bg-teal-50 text-sm font-semibold text-teal-800 transition-colors active:bg-teal-100">
          <Camera className="h-7 w-7" /> {side === "inside" ? "Inside" : "Outside"}</button>
      )}
    </div>
  );
};

// Phone photos are 20–50 MB: shrink to ~2000px JPEG (≈0.5–1.5 MB) — labels stay readable when zoomed
const shrink = (file) => new Promise((resolve) => {
  if (!file || !file.type?.startsWith("image/")) return resolve(file);
  const img = new window.Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const k = Math.min(1, 2000 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    c.toBlob((b) => resolve(b && b.size < file.size ? new File([b], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

let _names = null;
const usePackedBy = () => {
  const [names, setNames] = useState(_names || []);
  useEffect(() => {
    if (_names) return;
    axios.get(`${API}/order-register/packer/settings`, { headers: getAuthHeader() }).then((r) => { _names = r.data.packed_by_names || []; setNames(_names); }).catch(() => {});
  }, []);
  return names;
};

// AI photo check result (warn only — the photos are already saved)
const CheckNote = ({ id }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let n = 0, stop = false;
    const tick = async () => {
      try { const r = await axios.get(`${API}/order-register/queries/packing-check/${id}`, { headers: getAuthHeader() }); setD(r.data); if (r.data.status !== "checking") return; } catch { /* keep trying */ }
      if (!stop && ++n < 20) setTimeout(tick, 3000);
    };
    tick();
    return () => { stop = true; };
  }, [id]);
  if (!d || d.status === "checking") return <p className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600" data-testid="pk-check-wait"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking the product on the photo…</p>;
  if (d.match === "no") return (
    <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="pk-check-wrong">
      <b>Looks like a different product — please check and retake.</b> {d.note}
      {d.wrong?.length > 0 && <div>On photo, not in order: <b>{d.wrong.join(", ")}</b></div>}
      {d.missing?.length > 0 && <div>Not seen: <b>{d.missing.join(", ")}</b></div>}
      {d.qty_note && <div>Qty: {d.qty_note}</div>}
    </div>);
  return (
    <p className={`rounded-lg px-3 py-2 text-xs ${d.match === "yes" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid={`pk-check-${d.match}`}>
      {d.match === "yes" ? "✓ Product matches the order." : "Couldn't confirm the product from the photo."} {d.qty_note || d.note}</p>);
};

const PhotoSender = ({ p, onSent }) => {
  const [inside, setInside] = useState(null);
  const [outside, setOutside] = useState(null);
  const [busy, setBusy] = useState(false);
  const [by, setBy] = useState(() => localStorage.getItem("packed_by") || "");
  const [check, setCheck] = useState(null);
  const names = usePackedBy();
  const tid = `pk-${p.row_id}`;
  const send = async () => {
    if (names.length && !by) { toast.error("Pick your name in 'Packed by' first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (inside) fd.append("inside", await shrink(inside));
      if (outside) fd.append("outside", await shrink(outside));
      if (by) fd.append("packed_by", by);
      const r = await axios.post(`${API}/order-register/queries/by-row/${p.row_id}/packing-photos`, fd, { headers: getAuthHeader() });
      toast.success(`Photos sent for ${p.customer_name}`);
      setInside(null); setOutside(null);
      if (r.data?.check_id) setCheck(r.data.check_id); else onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      {names.length > 0 && (
        <select value={by} onChange={(e) => { setBy(e.target.value); localStorage.setItem("packed_by", e.target.value); }}
          className={`h-10 w-full rounded-xl border px-3 text-sm ${by ? "" : "border-amber-400 bg-amber-50"}`} data-testid={`${tid}-packed-by`}>
          <option value="">Packed by — pick your name</option>
          {names.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>)}
      <div className="flex gap-2"><Shot side="inside" file={inside} setFile={setInside} tid={tid} /><Shot side="outside" file={outside} setFile={setOutside} tid={tid} /></div>
      <button type="button" disabled={busy || (!inside && !outside)} onClick={send} data-testid={`${tid}-send`}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-teal-700 text-base font-semibold text-white disabled:opacity-40">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Send photos</button>
      {check && <><CheckNote id={check} /><button type="button" onClick={() => { setCheck(null); onSent(); }} className="w-full rounded-lg border py-1.5 text-xs font-semibold text-slate-600" data-testid={`${tid}-check-done`}>Done</button></>}
    </div>
  );
};

const Meta = ({ p, cols, labels }) => (
  <div className="flex flex-wrap gap-1.5 text-[11px]">
    {cols.filter(c => !["customer_name", "medicine", "second_name", "quantity"].includes(c) && p[c]).map(c => (
      <span key={c} className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700"><b className="font-semibold text-slate-500">{labels[c]}:</b> {p[c]}</span>))}
  </div>
);

const Items = ({ p }) => (
  <ul className="space-y-0.5 text-sm">
    {p.items.map((it, i) => (
      <li key={i} className="flex gap-2"><span className="font-medium text-slate-900">{it.medicine}</span>
        {it.second_name && <span className="text-slate-500">· {it.second_name}</span>}
        {it.quantity && <span className="ml-auto shrink-0 font-mono text-slate-700">×{it.quantity}</span>}</li>))}
  </ul>
);

const PhotoBadge = ({ n, id }) => (n ? (
  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid={`pk-photos-${id}`}>
    <CheckCircle2 className="h-3.5 w-3.5" /> {n} photo{n > 1 ? "s" : ""} sent</span>) : null);

const Card = ({ p, cols, labels, onSent }) => (
  <div className="space-y-2 rounded-2xl border bg-white p-3 shadow-sm" data-testid={`pk-card-${p.row_id}`}>
    <div className="flex items-start gap-2">
      <p className="text-lg font-bold leading-tight text-slate-900">{p.customer_name}</p>
      <span className="ml-auto"><PhotoBadge n={p.photos} id={p.row_id} /></span>
    </div>
    <Meta p={p} cols={cols} labels={labels} />
    <Items p={p} />
    <PhotoSender p={p} onSent={onSent} />
  </div>
);

const Table = ({ data, onCamera }) => (
  <div className="overflow-x-auto rounded-xl border bg-white">
    <table className="w-full text-xs" data-testid="pk-table">
      <thead><tr className="border-b bg-slate-50 text-left text-slate-500"><th className="w-10" />
        {data.columns.map(c => <th key={c} className="px-2 py-2 font-semibold">{data.labels[c]}</th>)}<th className="px-2">Photos</th></tr></thead>
      <tbody>{data.parcels.map(p => (
        <tr key={p.row_id} className="border-b last:border-0 align-top" data-testid={`pk-row-${p.row_id}`}>
          <td className="px-2 py-1.5"><button type="button" onClick={() => onCamera(p)} className="rounded-lg bg-teal-700 p-2 text-white" title="Take photos" data-testid={`pk-cam-${p.row_id}`}><Camera className="h-4 w-4" /></button></td>
          {data.columns.map(c => (
            <td key={c} className="px-2 py-1.5">{["medicine", "second_name", "quantity"].includes(c)
              ? p.items.map((it, i) => <div key={i}>{it[c]}</div>) : p[c]}</td>))}
          <td className="px-2 py-1.5"><PhotoBadge n={p.photos} id={`t-${p.row_id}`} /></td>
        </tr>))}</tbody>
    </table>
  </div>
);

// Red strip: AI said these photos show a different product and no new photo was sent yet
const RetakeBanner = ({ parcels, onOpen, tick }) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    const get = () => axios.get(`${API}/order-register/packer/retakes`, { headers: getAuthHeader() }).then((r) => setList(r.data.retakes || [])).catch(() => {});
    get();
    const t = setInterval(get, 60000);
    return () => clearInterval(t);
  }, [tick]);
  if (!list.length) return null;
  return (
    <div className="rounded-xl border-2 border-rose-400 bg-rose-50 p-3" data-testid="pk-retake-banner">
      <p className="mb-1 text-sm font-bold text-rose-800">Retake needed ({list.length}) — AI saw a different product</p>
      {list.map((r) => {
        const p = parcels.find((x) => x.row_id === r.row_id);
        return (
          <button key={r.id} type="button" onClick={() => p && onOpen(p)} disabled={!p}
            className={`mb-1 block w-full rounded-lg bg-white px-3 py-2 text-left text-xs ${r.minutes >= 30 ? "ring-2 ring-rose-500" : ""}`} data-testid={`pk-retake-${r.id}`}>
            <b>{r.customer_name || r.tracking}</b> · {r.minutes} min ago{r.by ? ` · ${r.by}` : ""}{r.minutes >= 30 ? " · OVERDUE" : ""}
            <div className="text-rose-700">{r.note}{r.wrong?.length ? ` On photo: ${r.wrong.join(", ")}` : ""}</div>
          </button>);
      })}
    </div>
  );
};

export default function PackerPage({ setIsAuthenticated }) {
  const me = JSON.parse(localStorage.getItem("user") || "{}");
  const [q, setQ] = useState("");
  const [view, setView] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "requests" ? "requests" : "camera"));
  const [reqOpen, setReqOpen] = useState(null);
  useEffect(() => {
    const poll = () => axios.get(`${API}/order-register/packer/photo-requests`, { headers: getAuthHeader(), params: { status: "open" } })
      .then(r => setReqOpen(r.data.open)).catch(() => setReqOpen(null));
    poll(); const t = setInterval(poll, 60000); return () => clearInterval(t);
  }, []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cam, setCam] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await axios.get(`${API}/order-register/packer/orders`, { headers: getAuthHeader(), params: { q } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load orders"); }
    setLoading(false);
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);
  const logout = () => {
    axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeader() }).catch(() => {});
    localStorage.removeItem("token"); localStorage.removeItem("user"); setIsAuthenticated(false);
  };
  const tab = (k, I, l) => (
    <button type="button" onClick={() => setView(k)} data-testid={`pk-view-${k}`}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-semibold transition-colors ${view === k ? "bg-white text-teal-800 shadow" : "text-teal-50"}`}>
      <I className="h-4 w-4" /> {l}
      {k === "requests" && reqOpen > 0 && <span className="rounded-full bg-rose-500 px-1.5 text-[11px] text-white" data-testid="pk-requests-badge">{reqOpen}</span>}</button>);
  return (
    <div className="min-h-screen bg-slate-100" data-testid="packer-page">
      <header className="sticky top-0 z-20 bg-teal-800 px-3 pb-3 pt-3 text-white shadow">
        <div className="mx-auto max-w-5xl space-y-2">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5" /><p className="font-bold">Packing Team</p>
          <span className="truncate text-xs text-teal-100">{me.name}</span>
          <button type="button" onClick={logout} className="ml-auto rounded-lg p-2 hover:bg-teal-700" title="Log out" data-testid="pk-logout"><LogOut className="h-4 w-4" /></button>
        </div>
        {view !== "requests" && <div className="relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client name" autoFocus
            className="h-12 rounded-xl border-0 bg-white pl-10 text-base text-slate-900" data-testid="pk-search" />
        </div>}
        <div className="flex gap-1 rounded-xl bg-teal-900/60 p-1">{tab("camera", Camera, "Camera")}{tab("list", LayoutList, "Order list")}
          {reqOpen !== null && tab("requests", ImageIcon, "Requests")}</div>
        </div>
      </header>
      {view === "requests" ? <main className="mx-auto max-w-5xl p-3"><PhotoRequestsList onCount={setReqOpen} /></main> : (
      <main className="mx-auto max-w-5xl space-y-3 p-3">
        <RetakeBanner parcels={data?.parcels || []} onOpen={setCam} tick={data} />
        <p className="text-xs text-slate-500" data-testid="pk-count">
          {loading ? "Searching…" : data ? `${data.parcels.length} parcel${data.parcels.length === 1 ? "" : "s"} · last 7 days + pending` : ""}</p>
        {data && !data.parcels.length && !loading && <p className="py-10 text-center text-sm text-slate-400" data-testid="pk-empty">No recent or pending parcel for this client</p>}
        {data && view === "camera" && <div className="grid gap-3 md:grid-cols-2">{data.parcels.map(p => <Card key={p.row_id} p={p} cols={data.columns} labels={data.labels} onSent={load} />)}</div>}
        {data && view === "list" && data.parcels.length > 0 && <Table data={data} onCamera={setCam} />}
      </main>)}
      <Dialog open={!!cam} onOpenChange={(o) => !o && setCam(null)}>
        <DialogContent className="max-w-md" data-testid="pk-cam-dialog">
          <DialogHeader><DialogTitle>{cam?.customer_name}</DialogTitle></DialogHeader>
          {cam && <><Items p={cam} /><PhotoSender p={cam} onSent={() => { setCam(null); load(); }} /></>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
